// SPDX-License-Identifier: MIT
// Mapbox configuration validation and style utilities

/**
 * Extracts the style ID from a Mapbox style URL
 * @param styleUrl - The full Mapbox style URL (e.g., "mapbox://styles/username/style-id")
 * @returns The style ID (e.g., "username/style-id") or undefined if invalid
 */
export function extractStyleIdFromUrl(styleUrl?: string): string | undefined {
  if (!styleUrl || !styleUrl.startsWith('mapbox://styles/')) {
    return undefined;
  }

  // Remove the "mapbox://styles/" prefix to get the style ID
  return styleUrl.replace('mapbox://styles/', '');
}

/**
 * Validates if a complete Mapbox configuration is available and valid
 * @returns {boolean} True if Mapbox token and style URL are both available
 */
export function getMapboxToken(): string | undefined {
  return import.meta.env.VITE_MAPBOX_TOKEN || undefined;
}

export function isValidMapboxConfig(): boolean {
  const token = getMapboxToken();
  const styleUrl = import.meta.env.VITE_MAPBOX_STYLE_URL;

  // Both token and style URL must be present and non-empty
  return Boolean(token && token.trim() && styleUrl && styleUrl.trim());
}

/**
 * Validates if a Mapbox token is present and appears to be valid format
 * @param {string} token - The Mapbox token to validate
 * @returns {boolean} True if token appears valid
 */
export function isValidMapboxToken(token?: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const trimmedToken = token.trim();

  // Basic Mapbox token format validation
  // Mapbox tokens typically start with 'pk.' for public tokens
  return (
    trimmedToken.length > 10 && (trimmedToken.startsWith('pk.') || trimmedToken.startsWith('sk.'))
  );
}

/**
 * Validates if a style URL is a valid Mapbox style URL
 * @param {string} styleUrl - The style URL to validate
 * @returns {boolean} True if URL appears to be a valid Mapbox style URL
 */
export function isValidMapboxStyleUrl(styleUrl?: string): boolean {
  if (!styleUrl || typeof styleUrl !== 'string') {
    return false;
  }

  // Check for valid Mapbox style URL patterns
  return styleUrl.startsWith('mapbox://styles/') || styleUrl.includes('api.mapbox.com/styles/');
}

/**
 * Gets the appropriate default style ID based on available configuration
 * @returns {string} The style ID to use as default
 */
export function getDefaultStyleId(): string {
  return isValidMapboxConfig() ? 'atlas_dark' : 'dark-matter';
}
